import { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import {
    Col, Container, Row, Form, Button, OverlayTrigger, Tooltip, Modal,
} from 'react-bootstrap';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import FormikControl from '../../../../layouts/Input/FormikControl';
import ImageUploader from './ImageUploader';
import LoanPreCheckModal from './LoanPreCheckModal';
import axios from '../../../../apis/Api';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { GET_LENDER_DDL, GET_LOANPLAN_DDL } from '../../../../Types';

import {AllowOnlyNumeric } from "../../../../utilities/Validations";
const initialValues = {
    lenderId: '',
    loanPlanId: '',
    deliveryQRCode: '',
    invoiceNumber: '',
    financeRequired: '',
    stockImageUrl: '',
    invoiceImageUrl: '',
};

const validationSchema = Yup.object({
    lenderId: Yup.string().required('Lender is required'),
    loanPlanId: Yup.string().required('Loan Plan is required'),
    deliveryQRCode: Yup.string().required('Delivery QR Code is required'),
    invoiceNumber: Yup.string().required('Invoice Number is required'),
    financeRequired: Yup.number()
        .positive('Must be greater than zero')
        .required('Finance amount is required'),
    stockImageUrl: Yup.string().required('Stock image is required'),
    invoiceImageUrl: Yup.string().required('Invoice image is required'),
});

const NewLoanForm = ({
    onClose,
    getDropdownList,
    allLenderList,
    allLoanPlanList,
    applyNewLoan,
}) => {
    const dispatch = useDispatch();
    const [loanPreCheckLoading, setLoanPreCheckLoading] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [loanInfo, setLoanInfo] = useState(null);
    const [allDLmOptions, setAllDLmOptions] = useState([]);

    useEffect(() => {
        const modalShown = localStorage.getItem('newLoanWelcomeModalShown');
        if (!modalShown) {
            const timer = setTimeout(() => {
                setShowWelcomeModal(true);
                localStorage.setItem('newLoanWelcomeModalShown', 'true');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    useEffect(() => {
        getDropdownList('api/Lender/GetLendersDDL', GET_LENDER_DDL);
    }, []);

    const lenderOptions = useMemo(() => allLenderList || [], [allLenderList]);
    const loanPlanOptions = useMemo(() => allLoanPlanList || [], [allLoanPlanList]);
    const dLMOptions = useMemo(() => allDLmOptions || [], [allDLmOptions]);

    const getSelectedOption = (options, value) =>
        options.find((opt) => opt.value === value) || '';

    const handleLenderChange = (value, setFieldValue) => {
        setFieldValue('lenderId', value.value);
        setFieldValue('loanPlanId', '');
        fetchLoanPlans(value.value);
        setLoanInfo(null);
    };

    const handleInvoiceChange = (value, setFieldValue) => {
        setFieldValue('invoiceNumber', value);
        setFieldValue('deliveryQRCode', '');
        setLoanInfo(null);
    };

    const handleQRCodeChange = (value, setFieldValue) => {
        setFieldValue('deliveryQRCode', value.value);
        setLoanInfo(null);
    };

    const fetchLoanPlans = (lenderId) => {
        if (lenderId) {
            getDropdownList(
                `api/Lender/GetLenderLoanPlansDDL/${lenderId}`,
                GET_LOANPLAN_DDL
            );
        }
    };

    const fetchAllDLMList = (invoiceNumber) => {
        if (invoiceNumber) {
            dispatch(showLoading());
            axios
                .get(`api/Lender/GetDLMListByInvoiceNumber/${invoiceNumber}`)
                .then(({ data }) => {
                    setAllDLmOptions(data?.response || []);
                    dispatch(hideLoading());
                })
                .catch(() => {
                    toast.error('Failed to load delivery QR codes.');
                    dispatch(hideLoading());
                });
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            await applyNewLoan(values, onClose);
        } catch (error) {
            toast.error(error?.message || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    const isFormValid = ({ lenderId, deliveryQRCode, invoiceNumber }) =>
        lenderId?.trim() && deliveryQRCode?.trim() && invoiceNumber?.trim();

    const handleLoanInfoFetch = async (formValues) => {
        if (!formValues) return;

        if (loanInfo) {
            setShowModal(true);
            return;
        }

        setLoanPreCheckLoading(true);
        dispatch(showLoading());

        try {
            const { data } = await axios.post(
                '/api/Lender/CreditBook/GetLoanPreCheckDetails',
                formValues
            );
            if (data.isSuccess) {
                setLoanInfo(data.response);
                setShowModal(true);
            } else {
                toast.error(data.message || 'Loan info fetch failed.');
            }
        } catch {
            toast.error('An error occurred while fetching loan info.');
        } finally {
            setLoanPreCheckLoading(false);
            dispatch(hideLoading());
        }
    };

    return (
        <>
            <Modal
                show={showWelcomeModal}
                onHide={() => setShowWelcomeModal(false)}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header>
                    <Modal.Title style={{ color: '#ff4d4d', animation: 'blinker 1.2s linear infinite' }}>
                        Important Notice
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><strong>Please read carefully before proceeding:</strong></p>
                    <p>
                        Ensure that all information in the loan application is accurate and complete.
                        Inaccurate or incomplete details may delay or affect approval.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowWelcomeModal(false)}>
                        I Understand
                    </Button>
                </Modal.Footer>
            </Modal>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Form autoComplete="off" onSubmit={formik.handleSubmit}>
                        <Container>
                            <Row>
                                <Col md={6}>
                                    <FormikControl
                                        formik={formik}
                                        control="react-select"
                                        label="Lender"
                                        name="lenderId"
                                        options={lenderOptions}
                                        handleOnChange={(val) =>
                                            handleLenderChange(val, formik.setFieldValue)
                                        }
                                    />
                                </Col>
                                <Col md={6}>
                                    <FormikControl
                                        formik={formik}
                                        control="react-select"
                                        label="Loan Plan"
                                        name="loanPlanId"
                                        options={loanPlanOptions}
                                        value={getSelectedOption(loanPlanOptions, formik.values.loanPlanId)}
                                        handleOnChange={(val) =>
                                            formik.setFieldValue('loanPlanId', val.value)
                                        }
                                    />
                                </Col>
                            </Row>

                            <Row className="mt-3">
                                <Col xs={6} md={4}>
                                    <FormikControl
                                        formik={formik}
                                        control="input"
                                        type="text"
                                        label="Invoice Number"
                                        name="invoiceNumber"
                                        maxLength={25}
                                        handleOnChange={(val) =>
                                            handleInvoiceChange(val, formik.setFieldValue)
                                        }
                                        onBlur={(e) => {
                                            formik.handleBlur(e);
                                            fetchAllDLMList(formik.values.invoiceNumber);
                                        }}
                                    />
                                </Col>
                                <Col xs={6} md={5}>
                                    <FormikControl
                                        formik={formik}
                                        control="react-select"
                                        label="Delivery QR Code"
                                        name="deliveryQRCode"
                                        options={dLMOptions}
                                        value={getSelectedOption(dLMOptions, formik.values.deliveryQRCode)}
                                        handleOnChange={(val) =>
                                            handleQRCodeChange(val, formik.setFieldValue)
                                        }
                                    />
                                </Col>
                                <Col xs={6} md={3}>
                                    <FormikControl
                                        formik={formik}
                                        control="input"
                                        type="text"                                       
                                        label="Finance Required"
                                        maxLength={11}
                                        value={formik.values.financeRequired}
                                        name="financeRequired"
                                        handleOnChange={(val) =>
                                            formik.setFieldValue('financeRequired', AllowOnlyNumeric(val))
                                        }
                                    />
                                </Col>
                            </Row>

                            <Row className="mt-3">
                                <Col md={6}>
                                    <ImageUploader
                                        fieldName="stockImageUrl"
                                        value={formik.values.stockImageUrl}
                                        setFieldValue={formik.setFieldValue}
                                        label="Select Stock Image"
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.stockImageUrl && formik.errors.stockImageUrl && (
                                        <div className="text-danger">{formik.errors.stockImageUrl}</div>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <ImageUploader
                                        fieldName="invoiceImageUrl"
                                        value={formik.values.invoiceImageUrl}
                                        setFieldValue={formik.setFieldValue}
                                        label="Select Invoice Image"
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.invoiceImageUrl && formik.errors.invoiceImageUrl && (
                                        <div className="text-danger">{formik.errors.invoiceImageUrl}</div>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mt-4">
                                <Col>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            (!formik.isValid || !formik.dirty) ? (
                                                <Tooltip id="submit-tooltip">
                                                    Please complete and correct all required fields
                                                </Tooltip>
                                            ) : <></>
                                        }
                                    >
                                        <span className="d-inline-block">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                disabled={formik.isSubmitting || loanPreCheckLoading
                                                    || !formik.isValid || !formik.dirty}
                                            >
                                                Submit Loan Application
                                            </Button>
                                        </span>
                                    </OverlayTrigger>{' '}
                                    <Button
                                        variant="secondary"
                                        onClick={onClose}
                                        disabled={formik.isSubmitting || loanPreCheckLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="info-tooltip">
                                                {isFormValid(formik.values)
                                                    ? 'Fetch pre-check loan info'
                                                    : 'Please fill in QR code, invoice number, and lender first.'}
                                            </Tooltip>
                                        }
                                    >
                                        <span className="d-inline-block float-end">
                                            <Button
                                                variant="outline-info"
                                                size="xs"
                                                disabled={!isFormValid(formik.values) || loanPreCheckLoading}
                                                onClick={() =>
                                                    isFormValid(formik.values)
                                                        ? handleLoanInfoFetch({
                                                            lenderId: formik.values.lenderId,
                                                            invoiceNumber: formik.values.invoiceNumber,
                                                            deliveryQRCode: formik.values.deliveryQRCode,
                                                        })
                                                        : toast.warn(
                                                            'Please fill in QR code, invoice number, and lender first.'
                                                        )
                                                }
                                                aria-label="Fetch loan info"
                                            >
                                                {loanPreCheckLoading ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <i className="fas fa-info-circle" />
                                                )}
                                            </Button>
                                        </span>
                                    </OverlayTrigger>
                                </Col>
                            </Row>
                        </Container>

                        {loanInfo && (
                            <LoanPreCheckModal
                                show={showModal}
                                handleClose={() => setShowModal(false)}
                                data={loanInfo}
                            />
                        )}
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default NewLoanForm;
